import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Upload,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Calendar,
  Database,
  TrendingUp,
  FileSpreadsheet,
  AlertCircle,
  Download,
  Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AhrefsUpdateTab = ({ language = 'es' }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [polling, setPolling] = useState(false);
  const [connectionTest, setConnectionTest] = useState(null);
  const [tempId, setTempId] = useState(null);
  const [downloadReady, setDownloadReady] = useState(false);
  const [downloads, setDownloads] = useState([]);

  const tx = (es, en) => language === 'es' ? es : en;

  useEffect(() => {
    fetchLastUpdate();
    fetchStatus();
    fetchDownloads();
  }, []);

  useEffect(() => {
    let interval;
    if (polling || status?.in_progress) {
      interval = setInterval(fetchStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [polling, status?.in_progress]);

  useEffect(() => {
    if (tempId && status && !status.in_progress && (status.updated > 0 || status.failed > 0)) {
      setDownloadReady(true);
      setPolling(false);
      fetchDownloads();
    }
  }, [status, tempId]);

  const fetchLastUpdate = async () => {
    try {
      const response = await axios.get(`${API}/ahrefs-api/last-update`);
      setLastUpdate(response.data);
    } catch (error) {
      console.error('Error fetching last update:', error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/ahrefs-api/status`);
      setStatus(response.data);
      if (!response.data.in_progress && polling) {
        setPolling(false);
        fetchLastUpdate();
        fetchDownloads();
        if (response.data.updated > 0) {
          toast.success(`${tx('Actualización completada', 'Update completed')}: ${response.data.updated} ${tx('actualizados', 'updated')}`);
        }
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchDownloads = async () => {
    try {
      const response = await axios.get(`${API}/ahrefs-api/downloads`);
      setDownloads(response.data.downloads || []);
    } catch (error) {
      console.error('Error fetching downloads:', error);
    }
  };

  const testConnection = async () => {
    try {
      const response = await axios.get(`${API}/ahrefs-api/test-connection`);
      setConnectionTest(response.data);
      if (response.data.status === 'success') {
        toast.success(tx('Conexión OK', 'Connection OK'));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(tx('Error de conexión', 'Connection error'));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      if (validTypes.includes(selectedFile.type) || 
          selectedFile.name.endsWith('.xlsx') || 
          selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
      } else {
        toast.error('Formato no válido. Usa Excel (.xlsx) o CSV');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error(tx('Selecciona un archivo primero', 'Select a file first'));
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/ahrefs-api/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${tx('Actualización iniciada', 'Update started')}: ${response.data.domains_count} ${tx('dominios encontrados', 'domains found')}`);
      setTempId(response.data.temp_id);
      setDownloadReady(false);
      setPolling(true);
      setFile(null);
      const fileInput = document.getElementById('ahrefs-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadJob = async (jobTempId, filename) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/ahrefs-api/download/${jobTempId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al descargar');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'ahrefs_updated.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(tx('Descarga completada', 'Download completed'));
    } catch (error) {
      toast.error(error.message || 'Error al descargar');
    }
  };

  const handleDeleteJob = async (jobTempId) => {
    if (!confirm(tx('¿Eliminar esta actualización?', 'Delete this update?'))) return;
    try {
      await axios.delete(`${API}/ahrefs-api/downloads/${jobTempId}`);
      toast.success(tx('Eliminado', 'Deleted'));
      fetchDownloads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return tx('Nunca', 'Never');
    const date = new Date(dateStr);
    return date.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const progressPercent = status?.total_products > 0 
    ? Math.round(((status.updated + status.failed) / status.total_products) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Last Update Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-blue-500" />
            {tx('Última actualización', 'Last update')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">{tx('Fecha', 'Date')}</p>
              <p className="text-lg font-semibold text-blue-600">
                {formatDate(lastUpdate?.last_update)}
              </p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">{tx('Actualizados', 'Updated')}</p>
              <p className="text-lg font-semibold text-green-600">
                {lastUpdate?.total_updated || 0}
              </p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">{tx('Fallidos', 'Failed')}</p>
              <p className="text-lg font-semibold text-red-600">
                {lastUpdate?.total_failed || 0}
              </p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-xs text-muted-foreground">{tx('Total', 'Total')}</p>
              <p className="text-lg font-semibold text-purple-600">
                {lastUpdate?.domains_count || 0}
              </p>
            </div>
          </div>

          {/* Red banner when last run was stopped because of exhausted API units
              (either explicitly flagged, OR heuristic: the run processed <50% of the total
              and almost nothing failed individually - classic "units ran out mid-way" shape). */}
          {(() => {
            const total = lastUpdate?.domains_count || 0;
            const ok = lastUpdate?.total_updated || 0;
            const fail = lastUpdate?.total_failed || 0;
            const pending = Math.max(total - ok - fail, 0);
            const isInterrupted =
              lastUpdate?.stopped_reason === 'units_exhausted' ||
              (total >= 500 && pending / total >= 0.5 && fail / Math.max(total, 1) < 0.05);
            if (!isInterrupted) return null;
            return (
              <div
                className="mt-4 flex items-start gap-3 border-2 border-red-300 bg-red-50 rounded-lg p-4"
                data-testid="ahrefs-units-exhausted-banner"
              >
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">
                    {tx('Créditos de Ahrefs agotados', 'Ahrefs API credits exhausted')}
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {tx(
                      `La actualización se detuvo tras procesar ${ok} de ${total} dominios. Quedan ${pending} sin procesar. Compra más créditos o espera a que Ahrefs renueve tu plan y vuelve a subir el Excel con los dominios pendientes.`,
                      `The update stopped after processing ${ok} of ${total} domains. ${pending} are still pending. Buy more credits or wait for Ahrefs to renew your plan, then re-upload the Excel with the pending domains.`
                    )}
                  </p>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <a
                      href="https://app.ahrefs.com/subscription"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-red-700"
                      data-testid="ahrefs-buy-credits-link"
                    >
                      {tx('Gestionar plan Ahrefs', 'Manage Ahrefs plan')}
                    </a>
                  </div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {tx('Actualización Ahrefs', 'Ahrefs Update')}
          </CardTitle>
          <CardDescription>{tx('Actualiza DR y Tráfico Orgánico de tu catálogo', 'Update DR and Organic Traffic for your catalog')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Connection Test */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={testConnection} data-testid="ahrefs-test-connection">
              <Database className="h-4 w-4 mr-2" />
              {tx('Probar Conexión', 'Test Connection')}
            </Button>
            {connectionTest && (
              <Badge variant={connectionTest.status === 'success' ? 'default' : 'destructive'}>
                {connectionTest.status === 'success' ? (
                  <><CheckCircle2 className="h-3 w-3 mr-1" /> DR: {connectionTest.test_dr}</>
                ) : (
                  <><XCircle className="h-3 w-3 mr-1" /> Error</>
                )}
              </Badge>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>{tx('Subir Excel con dominios', 'Upload Excel with domains')}</Label>
            <div className="flex gap-2">
              <Input
                id="ahrefs-file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="flex-1"
                data-testid="ahrefs-file-input"
              />
              <Button 
                onClick={handleUpload} 
                disabled={!file || uploading || status?.in_progress}
                data-testid="ahrefs-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{tx('Actualizando...', 'Updating...')}</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />{tx('Iniciar Actualización', 'Start Update')}</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <FileSpreadsheet className="h-3 w-3" />
              {tx('Formato: Excel (.xlsx) o CSV con columna "website" o "domain"', 'Format: Excel (.xlsx) or CSV with "website" or "domain" column')}
            </p>
            {file && (
              <Badge variant="outline" className="bg-blue-50">{file.name}</Badge>
            )}
          </div>

          {/* Progress */}
          {status?.in_progress && (
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tx('Actualización en progreso', 'Update in progress')}
                </span>
                <span className="text-sm text-blue-600">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-blue-600">
                <span>{tx('Actualizados', 'Updated')}: {status.updated}</span>
                <span>{tx('Fallidos', 'Failed')}: {status.failed}</span>
                <span>Total: {status.total_products}</span>
              </div>
            </div>
          )}

          {/* Refresh + Errors Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchLastUpdate(); fetchDownloads(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {tx('Actualizar estado', 'Refresh status')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="ahrefs-download-errors-btn"
              disabled={!status || (status.failed || 0) === 0}
              onClick={async () => {
                try {
                  const response = await fetch(`${API}/ahrefs-api/errors/excel`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                  });
                  if (!response.ok) {
                    const text = await response.text();
                    toast.error(tx('No hay errores para descargar', 'No errors to download') + `: ${text.slice(0, 120)}`);
                    return;
                  }
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').slice(0, 15);
                  link.download = `ahrefs_errors_${stamp}.xlsx`;
                  link.click();
                  URL.revokeObjectURL(url);
                  toast.success(tx('Excel de errores descargado', 'Errors Excel downloaded'));
                } catch (e) {
                  toast.error(tx('Error descargando', 'Error downloading'));
                }
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              {tx('Descargar errores', 'Download errors')}
              {status && (status.failed || 0) > 0 && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-2 py-0.5">
                  {status.failed}
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Downloads History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            {tx('Historial de actualizaciones', 'Update history')}
          </CardTitle>
          <CardDescription>{tx('Descarga o elimina actualizaciones anteriores', 'Download or delete previous updates')}</CardDescription>
        </CardHeader>
        <CardContent>
          {downloads.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {tx('No hay actualizaciones guardadas', 'No saved updates')}
            </p>
          ) : (
            <div className="space-y-3">
              {downloads.map((dl) => {
                const filename = dl.original_filename || 'ahrefs_updated.xlsx';
                const baseName = filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
                return (
                  <div key={dl.temp_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`ahrefs-job-${dl.temp_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{filename}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                        <span>{formatDate(dl.updated_at)}</span>
                        {dl.stats && (
                          <>
                            <span className="text-green-600">{dl.stats.updated} {tx('actualizados', 'updated')}</span>
                            <span className="text-red-600">{dl.stats.failed} {tx('fallidos', 'failed')}</span>
                            <span>Total: {dl.stats.total}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-3 shrink-0">
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleDownloadJob(dl.temp_id, `${baseName}_ahrefs_updated.xlsx`)}
                        data-testid={`ahrefs-download-${dl.temp_id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {tx('Descargar', 'Download')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteJob(dl.temp_id)}
                        data-testid={`ahrefs-delete-${dl.temp_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AhrefsUpdateTab;
